import { useMemo, useState } from 'react'

import { useParams } from 'react-router-dom'

import {
  useGetMetaAdsCampaignByIdQuery,
  useUpdateMetaAdsCampaignMutation,
} from 'src/Redux/adsCampaignApi'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'

import { AdCampaignCountriesTierEnum, ADS_COUNTRIES } from './helpers'

type UseAdsCountries = {
  countriesTier: AdCampaignCountriesTierEnum | undefined
  selectedCountries: string[]
  onCountryClick: (country: string) => void
  onSaveCountries: () => void
  setCountriesTier: (tier: AdCampaignCountriesTierEnum) => void
  disabledAdd: boolean
  onClose: () => void
}

export const useAdsCountries = (): UseAdsCountries => {
  const { number } = useParams()
  const { data } = useGetMetaAdsCampaignByIdQuery(Number(number))
  const { setSearch } = useCustomSearchParams()
  const [updateMetaAdsCampaign] = useUpdateMetaAdsCampaignMutation()

  const [localCountriesTier, setLocalCountriesTier] = useState<
    AdCampaignCountriesTierEnum | undefined
  >()

  const [localSelectedCountries, setLocalSelectedCountries] = useState<
    string[] | undefined
  >()

  const countriesTier = localCountriesTier ?? data?.countriesTier

  const selectedCountries = useMemo(
    () =>
      localSelectedCountries ??
      (data?.countriesTier === AdCampaignCountriesTierEnum.CUSTOM
        ? data.countries
        : []),
    [localSelectedCountries, data],
  )

  const onClose = (): void => {
    setSearch({ modal: null })
  }

  const setCountriesTier = (tier: AdCampaignCountriesTierEnum): void => {
    setLocalCountriesTier(tier)

    if (tier !== AdCampaignCountriesTierEnum.CUSTOM) {
      setLocalSelectedCountries([])
    }
  }

  const onCountryClick = (country: string): void => {
    setLocalSelectedCountries((prev) => {
      const currentCountries = prev ?? selectedCountries

      return currentCountries.includes(country)
        ? currentCountries.filter((c) => c !== country)
        : [...currentCountries, country]
    })
  }

  const onSaveCountries = (): void => {
    if (!countriesTier) {
      return
    }

    const countries =
      countriesTier === AdCampaignCountriesTierEnum.CUSTOM
        ? selectedCountries
        : ADS_COUNTRIES[countriesTier]

    updateMetaAdsCampaign({
      id: Number(number),
      countries,
      countriesTier,
    })
      .unwrap()
      .then(() => {
        onClose()
      })
  }

  const disabledAdd = useMemo(() => {
    if (countriesTier === AdCampaignCountriesTierEnum.CUSTOM) {
      return selectedCountries.length === 0
    }

    return !countriesTier
  }, [countriesTier, selectedCountries])

  return {
    countriesTier,
    selectedCountries,
    disabledAdd,
    onCountryClick,
    setCountriesTier,
    onSaveCountries,
    onClose,
  }
}
